import React, { useEffect, useState, useContext, useCallback } from 'react'
import styles from './toggle-vouchers.module.scss'
import Button from 'src/components/button/Button'
import { baseApiURL } from 'src/utils/utils'
import { StateContext, DispatchContext } from '../../AppContext'
import { IState } from 'src/Interface'
import axios from 'axios'
import { toast } from 'react-toastify'
import ToggleSwitch from 'src/components/toggleSwitch/ToggleSwitch'
import Input from 'src/components/input/Input'
import useCancellablePromise from 'src/utils/useCancellablePromise'

interface IProps {
  close?: any
  companyId: number,
  branchId: number
  fetchVouchers: () => void
  fetchBranches: () => void
}

const LocationDetails: React.FunctionComponent<IProps> = ({ close, companyId, branchId, fetchVouchers, fetchBranches }: IProps) => {
  const state: IState = useContext(StateContext)
  const dispatch: React.Dispatch<any> = useContext(DispatchContext)
  const validId = /^\d{1,6}$/
  const { cancellablePromise } = useCancellablePromise()
  const [validationErrors, setValidationErrors] = useState({
    accountNumber: false
  })
  const [errors, setErrors] = useState({
    selectedAccount: false,
    voucherEnabled: false
  })
  const [selectedAccount, setSelectedAccount] = useState(state.branch.vouchersAccount)
  const [voucherEnabled, toggleVoucher] = useState(!!state.branch.vouchersAccount)

  useEffect(() => {
    if (state.user.accessToken && companyId) {
      cancellablePromise(axios(`${baseApiURL()}/companies/${companyId}/branches/${branchId}`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then((result: any) => {
        dispatch({ type: 'SET_BRANCH', branch: result.data })
        setSelectedAccount(result.data.vouchersAccount)
        toggleVoucher(!!result.data.vouchersAccount)
      }).catch((err: any) => {
        toast(err.response.data.message)
      }))
    }
  }, [branchId, companyId, dispatch, state.company.id, state.user.accessToken, cancellablePromise])


  const handleValidation = (regexp: RegExp, fieldName: string) => (value: string) => {
    let error = false
    if (value && regexp && !regexp.test(value || '')) {
      error = true
    }

    setValidationErrors({
      ...validationErrors,
      [fieldName]: error
    })
  }

  const enableVoucher = () => {
    if (state.branch.vouchersAccount) {
      axios.put(`${baseApiURL()}/branches/${state.branch.id}/vouchers/account`,
        {
          code: selectedAccount
        }, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then(() => {
        close()
        if (fetchBranches) {
          fetchBranches()
        }

        if (fetchVouchers) {
          fetchVouchers()
        }
      }).catch((err: any) => {
        toast(err.response.data.message)
      })
    } else {
      axios.post(`${baseApiURL()}/branches/${state.branch.id}/vouchers/account`,
        {
          code: selectedAccount
        }, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then(() => {
        close()

        if (fetchBranches) {
          fetchBranches()
        }

        if (fetchVouchers) {
          fetchVouchers()
        }
      }).catch((err: any) => {
        toast(err.response.data.message)
      })
    }
  }

  const disableVouchers = useCallback(() => {
    axios.delete(`${baseApiURL()}/branches/${state.branch.id}/vouchers/account`,
      {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then(() => {
        dispatch({ type: 'SHOW_MODAL', modalType: '', showModal: false, })
        close()
        if (fetchVouchers) {
          fetchVouchers()
        }
        if (fetchBranches) {
          fetchBranches()
        }
      }).catch((err: any) => {
        toast(err.response.data.message)
      })
  }, [close, dispatch, state.branch.id, state.user.accessToken])

  const updateVoucher = () => {
    if (voucherEnabled && !selectedAccount) {
      setErrors({
        selectedAccount: true,
        voucherEnabled: false
      })
      return null
    }
    if (voucherEnabled) {
      enableVoucher()
    } else if (state.branch.vouchersAccount) {
      dispatch({ type: 'SHOW_MODAL', modalType: 'deleteConfirmation', showModal: true, title: 'Er du sikker på at deaktivere Taxibon?', text: 'Alle nuværende taxibonner vil blive annulleret', buttonText: 'Deaktiver taxibon', handleDelete: disableVouchers })
    } else {
      disableVouchers()
    }
  }

  const resetError = () => {
    setValidationErrors({
      accountNumber: false
    })
    setErrors({
      voucherEnabled: false,
      selectedAccount: false
    })
  }

  return (
    <>
      <header className={styles.header}>
        <h4>Taxibon</h4>
        <svg onClick={close} xmlns="http://www.w3.org/2000/svg" className="tw-h-5 tw-w-5 tw-ml-auto tw-cursor-pointer" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </header>
      <div className={styles.container}>
        <div className={styles.toggleSwitch}>
          <ToggleSwitch label={voucherEnabled ? 'Aktiveret' : 'Aktivér Taxibon'} textSize='large' onChange={toggleVoucher} checked={voucherEnabled} />
        </div>
        <Input resetError={resetError} type='text' handleValidation={() => handleValidation(validId, 'accountNumber')} error={errors.selectedAccount || validationErrors.accountNumber} value={selectedAccount} name='accountDropdown' placeholder='indtast kontonummer' label='Kontonummer *' handleInputChange={() => setSelectedAccount} />
        {errors.selectedAccount && <p className='errorMsg'>Udfyldt venligst kontonummer</p>}
        {validationErrors.accountNumber && <p className='errorMsg'>Bør kun bestå af tal og være mellem 1 og 6 karakterer.</p>}
      </div>
      <footer className={styles.footer}>
        <Button type='secondary' label='Annuller' modifiers='tw-mr-auto' handleClick={close} />
        <Button disabled={!state.branch.vouchersAccount && !voucherEnabled} type='primary' label='Gem ændringer' handleClick={updateVoucher} />
      </footer>
    </>
  )
}

export default LocationDetails
